import { DEFAULT_NODE_SIZE } from './nodeGeometryDefaults';
export const VIEWPORT_CULLING_THRESHOLD = 120;
export const VIEWPORT_CULLING_OVERSCAN_PX = 600;

function isFiniteViewport(viewport) {
  return Boolean(
    viewport
    && Number.isFinite(viewport.scale)
    && viewport.scale > 0
    && Number.isFinite(viewport.offset?.x)
    && Number.isFinite(viewport.offset?.y)
  );
}

function isFiniteViewportSize(viewportSize) {
  return Boolean(
    viewportSize
    && Number.isFinite(viewportSize.width)
    && viewportSize.width > 0
    && Number.isFinite(viewportSize.height)
    && viewportSize.height > 0
  );
}

function rectIntersectsBounds(rect, bounds) {
  return !(
    rect.x + rect.width < bounds.minX
    || rect.x > bounds.maxX
    || rect.y + rect.height < bounds.minY
    || rect.y > bounds.maxY
  );
}

export function getOverscannedWorldBounds({
  viewport,
  viewportSize,
  overscanPx = VIEWPORT_CULLING_OVERSCAN_PX,
} = {}) {
  if (!isFiniteViewport(viewport) || !isFiniteViewportSize(viewportSize)) {
    return null;
  }
  const safeOverscan = Math.max(0, Number.isFinite(overscanPx) ? overscanPx : 0);
  return {
    minX: (-safeOverscan - viewport.offset.x) / viewport.scale,
    minY: (-safeOverscan - viewport.offset.y) / viewport.scale,
    maxX: (viewportSize.width + safeOverscan - viewport.offset.x) / viewport.scale,
    maxY: (viewportSize.height + safeOverscan - viewport.offset.y) / viewport.scale,
  };
}

export function deriveViewportMountedNodeIds({
  nodes = [],
  branchVisibleNodeIds = new Set(),
  retainedNodeIds = new Set(),
  viewport,
  viewportSize,
  overscanPx = VIEWPORT_CULLING_OVERSCAN_PX,
  threshold = VIEWPORT_CULLING_THRESHOLD,
  getNodeRect,
  spatialIndex = null,
} = {}) {
  const visibleIds = branchVisibleNodeIds instanceof Set
    ? branchVisibleNodeIds
    : new Set(branchVisibleNodeIds || []);
  const safeThreshold = Math.max(0, Number.isFinite(threshold) ? threshold : 0);
  const bounds = getOverscannedWorldBounds({ viewport, viewportSize, overscanPx });

  if (visibleIds.size <= safeThreshold || !bounds) {
    return new Set(visibleIds);
  }

  const mountedIds = new Set();
  const candidates = typeof spatialIndex?.query === 'function'
    ? spatialIndex.query(bounds)
    : (Array.isArray(nodes) ? nodes : []);
  for (const node of candidates) {
    if (!node?.id || !visibleIds.has(node.id)) continue;
    const fallbackPosition = node.position || { x: 0, y: 0 };
    const fallbackSize = node.layoutSize || DEFAULT_NODE_SIZE;
    const candidate = typeof getNodeRect === 'function' ? getNodeRect(node) : null;
    const rect = {
      x: Number.isFinite(candidate?.x) ? candidate.x : fallbackPosition.x,
      y: Number.isFinite(candidate?.y) ? candidate.y : fallbackPosition.y,
      width: Number.isFinite(candidate?.width) ? candidate.width : fallbackSize.width,
      height: Number.isFinite(candidate?.height) ? candidate.height : fallbackSize.height,
    };
    if (rectIntersectsBounds(rect, bounds)) mountedIds.add(node.id);
  }

  for (const nodeId of retainedNodeIds || []) {
    if (visibleIds.has(nodeId)) mountedIds.add(nodeId);
  }

  return mountedIds;
}

export function deriveViewportMountedRelationships(
  relationships = [],
  mountedNodeIds = new Set()
) {
  return (Array.isArray(relationships) ? relationships : []).filter(
    ({ parentId, childId }) => (
      mountedNodeIds.has(parentId) && mountedNodeIds.has(childId)
    )
  );
}
